import React from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '../ui/button';

interface CoursePlayerShellProps {
    title: string;
    categoryName: string | null;
    categoryColour: string | null;
    contentTypeLabel: string;
    onExit: () => void;
    children: React.ReactNode;
    completed?: boolean;
}

/**
 * Player chrome shared by every content-type body (LEARNING.md §7 / §13.1).
 *
 * Nothing plays content here: the shell owns the exit control, the title / category /
 * content-type line and the body slot. There is deliberately NO progress bar — with no
 * SCORM runtime there is nothing measuring how far a learner has got, and a bar that only
 * ever reads 0 or 100 reads as broken. State is conveyed by the Completed pill and the
 * status on the register / My Learning instead. When SCORM lands, the bar can come back
 * with a real number behind it; that is a header change, not a redesign.
 *
 * Category colour is data, so it is the one thing applied as an inline style; everything
 * else uses semantic tokens.
 */
export const CoursePlayerShell: React.FC<CoursePlayerShellProps> = ({
    title,
    categoryName,
    categoryColour,
    contentTypeLabel,
    onExit,
    children,
    completed = false,
}) => {
    return (
        <div className="mx-auto w-full max-w-5xl overflow-hidden rounded-xl border border-border bg-card">
            {/* ─── Chrome: stacks on small screens, one row from md up ─── */}
            <div className="flex flex-col gap-3 border-b border-border bg-card p-4 md:flex-row md:items-center">
                <Button variant="outline" size="sm" onClick={onExit} className="w-fit">
                    <ArrowLeft size={14} /> Exit
                </Button>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-semibold">{title}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                        {categoryName && (
                            <span
                                className="rounded-full border bg-white px-2 py-0.5 text-xs font-semibold"
                                style={
                                    categoryColour
                                        ? { color: categoryColour, borderColor: categoryColour }
                                        : undefined
                                }
                            >
                                {categoryName}
                            </span>
                        )}
                        <span className="text-xs text-muted-foreground">{contentTypeLabel}</span>
                        {completed && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                                <Check size={12} /> Completed
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* ─── Content-type body ─── */}
            <div className="flex flex-col gap-3.5 bg-muted/30 p-4">{children}</div>
        </div>
    );
};

export default CoursePlayerShell;
